import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, AlertCircle, Download } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useRoles } from '@/hooks/useRoles';

interface LogoResult {
  tool: string;
  status: 'success' | 'already_exists' | 'not_found' | 'upload_failed' | 'error';
  source?: string;
  error?: string;
}

interface FetchResponse {
  success: boolean;
  processed: number;
  results: LogoResult[];
}

export default function AdminLogos() {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<LogoResult[]>([]);
  const [progress, setProgress] = useState(0);
  const { toast } = useToast();
  const { isAdmin, loading: rolesLoading } = useRoles();

  // Check if user is admin
  if (rolesLoading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin()) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Access denied. This page is restricted to administrators only.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const handleFetchLogos = async () => {
    setIsLoading(true);
    setResults([]);
    setProgress(0);

    try {
      const { data, error } = await supabase.functions.invoke('fetch-tool-logos');

      if (error) {
        throw new Error(error.message);
      }

      const response = data as FetchResponse;
      setResults(response.results || []);
      setProgress(100);

      const successCount = response.results?.filter(r => r.status === 'success').length || 0;
      const alreadyExistsCount = response.results?.filter(r => r.status === 'already_exists').length || 0;
      
      toast({
        title: "Logo fetch completed",
        description: `${successCount} new logos fetched, ${alreadyExistsCount} already existed`,
      });

    } catch (error) {
      console.error('Error fetching logos:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to fetch logos",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'already_exists':
        return <CheckCircle className="h-4 w-4 text-blue-500" />;
      case 'not_found':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'upload_failed':
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'success':
        return 'Successfully fetched';
      case 'already_exists':
        return 'Already exists';
      case 'not_found':
        return 'Logo not found';
      case 'upload_failed':
        return 'Upload failed';
      case 'error':
        return 'Error occurred';
      default:
        return status;
    }
  };

  const successCount = results.filter(r => r.status === 'success').length;
  const errorCount = results.filter(r => r.status === 'error' || r.status === 'upload_failed').length;
  const notFoundCount = results.filter(r => r.status === 'not_found').length;
  const existingCount = results.filter(r => r.status === 'already_exists').length;

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Logo Management</h1>
        <p className="text-text-secondary">
          Fetch and update official logos for all tools in the database.
        </p>
      </div>

      <Alert className="mb-6">
        <Download className="h-4 w-4" />
        <AlertDescription>
          This will fetch logos from multiple sources (Clearbit, favicons) and store them in Supabase Storage.
          Existing logos will be skipped.
        </AlertDescription>
      </Alert>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Fetch Tool Logos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button 
            onClick={handleFetchLogos} 
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            {isLoading ? 'Fetching Logos...' : 'Fetch All Logos'}
          </Button>

          {isLoading && (
            <div className="space-y-2">
              <Progress value={progress} className="w-full" />
              <p className="text-sm text-text-secondary">
                Processing tool logos...
              </p>
            </div>
          )}

          {results.length > 0 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{successCount}</div>
                  <div className="text-sm text-green-700 dark:text-green-400">Success</div>
                </div>
                <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{existingCount}</div>
                  <div className="text-sm text-blue-700 dark:text-blue-400">Existing</div>
                </div>
                <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">{notFoundCount}</div>
                  <div className="text-sm text-yellow-700 dark:text-yellow-400">Not Found</div>
                </div>
                <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{errorCount}</div>
                  <div className="text-sm text-red-700 dark:text-red-400">Errors</div>
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto space-y-2">
                {results.map((result, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-surface rounded-lg">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(result.status)}
                      <span className="font-medium">{result.tool}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium">{getStatusText(result.status)}</div>
                      {result.source && (
                        <div className="text-xs text-text-secondary truncate max-w-48">
                          {result.source}
                        </div>
                      )}
                      {result.error && (
                        <div className="text-xs text-red-500 truncate max-w-48">
                          {result.error}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}